import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { Subscription } from '../types';
import { apiService } from '../services/api';
import { cognitoAuthService } from '../services/cognitoAuth';

type SubscriptionStatus =
  | 'trial'
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'expired';

interface SubscriptionContextType {
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;

  // Derived helpers
  hasValidSubscription: () => boolean;
  isTrialActive: () => boolean;
  hasUsedTrial: () => boolean;
  getSubscriptionStatus: () => SubscriptionStatus;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated
      const authenticated = await cognitoAuthService.isAuthenticated();

      if (!authenticated) {
        setSubscription(null);
        setLoading(false);
        return;
      }

      // Fetch subscription
      const subscriptionData = await apiService.getSubscription();
      setSubscription(subscriptionData.subscription);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to load subscription');
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const refetch = async () => {
    await fetchSubscription();
  };

  // Helper: Check if user has used trial (subscription exists)
  const hasUsedTrial = (): boolean => {
    return subscription !== null;
  };

  // Helper: Check if trial is active
  const isTrialActive = (): boolean => {
    if (!subscription || subscription.type !== 'TRIAL') {
      return false;
    }

    const now = new Date();
    const endDate = new Date(subscription.currentPeriodEnd);
    return now < endDate;
  };

  // Helper: Check if canceled subscription is still valid (within billing period)
  const isCanceledButStillValid = (): boolean => {
    if (!subscription) {
      return false;
    }

    if (!subscription.cancelAtPeriodEnd) {
      return false;
    }

    const now = new Date();
    const periodEnd = new Date(subscription.currentPeriodEnd);
    return now < periodEnd;
  };

  // Helper: Check if user has a valid subscription
  const hasValidSubscription = (): boolean => {
    if (!subscription) {
      return false;
    }

    // Trial subscription
    if (subscription.type === 'TRIAL') {
      return isTrialActive();
    }

    // Active paid subscription
    if (subscription.status === 'active') {
      return true;
    }

    // Canceled subscription still within billing period
    if (subscription.status === 'canceled') {
      return isCanceledButStillValid();
    }

    return false;
  };

  // Helper: Get subscription status for display
  const getSubscriptionStatus = (): SubscriptionStatus => {
    // Return expired if no subscription exists
    if (!subscription) {
      return 'expired';
    }

    if (subscription.type === 'TRIAL') {
      return isTrialActive() ? 'trial' : 'expired';
    }

    // Paid subscription - map status
    const statusMap: Record<string, SubscriptionStatus> = {
      active: 'active',
      past_due: 'past_due',
      canceled: 'canceled',
      expired: 'expired',
    };

    return statusMap[subscription.status] || 'expired';
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        error,
        refetch,
        hasValidSubscription,
        isTrialActive,
        hasUsedTrial,
        getSubscriptionStatus,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider'
    );
  }
  return context;
};
